import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { submitAuditToWebhook } from '@/lib/webhook';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const normalizeWebsiteUrl = (raw) => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

export const FreeAudit = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    acceptedTerms: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current) {
        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setTurnstileToken(null),
        });

        return () => {
          if (window.turnstile && widgetId) {
            window.turnstile.remove(widgetId);
          }
        };
      }
    };

    if (document.readyState === 'complete') {
      return renderWidget();
    }

    const onLoad = () => {
      renderWidget();
    };

    window.addEventListener('load', onLoad);
    return () => window.removeEventListener('load', onLoad);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!formData.acceptedTerms) {
      toast.error('Please accept the terms and privacy policy before submitting.');
      return;
    }
    if (!turnstileToken) {
      toast.error('Please complete the security check before submitting.');
      return;
    }

    const normalizedWebsite = normalizeWebsiteUrl(formData.website);

    setIsSubmitting(true);
    
    try {
      const result = await submitAuditToWebhook({
        name: formData.name,
        email: formData.email,
        website: normalizedWebsite,
        acceptedTerms: formData.acceptedTerms,
        turnstileToken
      });

      const text = (result || '').toString();
      const firstLine = text.split('\n')[0]?.trim() || '';
      const lower = firstLine.toLowerCase();

      if (lower.startsWith('#message: invalid_domain')) {
        const badUrl = normalizedWebsite || formData.website;
        const name = formData.name || 'there';
        toast.error(
          `${name}, it looks like "${badUrl}" is not a valid website URL we can audit. Please check it and try again.`
        );
        return;
      }

      // Also store locally as backup
      const audits = JSON.parse(localStorage.getItem('auditRequests') || '[]');
      audits.push({
        ...formData,
        website: normalizedWebsite,
        timestamp: new Date().toISOString(),
        id: Date.now()
      });
      localStorage.setItem('auditRequests', JSON.stringify(audits));
      
      setSubmitted(true);
      toast.success('Audit request submitted successfully!');
    } catch (error) {
      console.error('Error submitting audit request:', error);
      toast.error('Failed to submit audit request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'email') {
      if (!value) {
        setEmailError('Email is required.');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        setEmailError('Please enter a valid email address.');
      } else {
        setEmailError('');
      }
    }
  };

  const benefits = [
    'AI Visibility Snapshot: Where and how your brand is currently cited (or not) in AI engines',
    'Competitor Comparison: Which competitors are being recommended in your place',
    'Quick Wins: 3-5 high-impact recommendations you can implement right away',
    'Roadmap Options: A glimpse of what a full GEO/AIO strategy could look like'
  ];

  const steps = [
    'Fill in the short form with your details',
    'We run manual + tool-assisted analysis',
    'Receive clear PDF report by email within 3-5 business days',
    'Optional: Discuss implementation through our retainers'
  ];

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6 overflow-hidden">
            <img
              src="/assets/evolutionlogo-light-mode.png"
              alt="Evolution GEO"
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            Thank You!
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            We've received your audit request. Our team will analyze your AI visibility and send you a comprehensive report to your email within about 1 hour.
          </p>
          <div className="bg-muted/50 rounded-lg p-6 mb-8">
            <h3 className="font-display font-semibold mb-2">What Happens Next?</h3>
            <ol className="text-left space-y-2 text-muted-foreground">
              <li>1. Our team reviews your brand and competitors</li>
              <li>2. We analyze your presence across AI engines</li>
              <li>3. We compile actionable recommendations</li>
              <li>4. You receive your audit via email</li>
            </ol>
          </div>
          <p className="text-sm text-muted-foreground">
            Check your email (including spam folder) for our report.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-10 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            Free <span className="text-gradient">GEO/AIO Audit</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground mb-3">
            Valued at <span className="font-semibold">$2,000+</span>
          </p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See how your brand appears today in ChatGPT, Perplexity, Google AI Overviews, and other AI engines—plus get a clear roadmap to improve.
          </p>
        </div>
      </section>

      {/* Two-column content + form */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:items-start">
            {/* Left: What you get + How it works */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-6">
                What You Get
              </h2>
              <div className="space-y-4 mb-10">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="border-border">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex items-start space-x-3">
                        <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <p className="text-sm sm:text-base text-foreground">{benefit}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <h3 className="text-xl sm:text-2xl font-display font-semibold mb-4">
                How It Works
              </h3>
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center flex-shrink-0 text-primary-foreground text-sm font-bold">
                      {index + 1}
                    </div>
                    <p className="text-sm sm:text-base text-foreground pt-0.5">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <Card className="border-border shadow-lg">
                <CardHeader>
                  <CardTitle className="font-display text-2xl">Request Your Free Audit</CardTitle>
                  <CardDescription>
                    Share a few details so we can review your site and send your GEO/AIO audit.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Work Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                      />
                      {emailError ? (
                        <p className="text-xs text-destructive mt-1">{emailError}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Please double‑check this email – your audit will be delivered here.
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website">Website URL *</Label>
                      <div className="rounded-lg border border-primary/70 bg-primary/5 px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-1 focus-within:ring-offset-background transition">
                        <Input
                          id="website"
                          name="website"
                          type="text"
                          required
                          value={formData.website}
                          onChange={handleChange}
                          placeholder="https://yourwebsite.com"
                          className="border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        We’ll review this site for your GEO/AIO audit.
                      </p>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="terms"
                        checked={formData.acceptedTerms}
                        onCheckedChange={(checked) =>
                          setFormData(prev => ({ ...prev, acceptedTerms: !!checked }))
                        }
                      />
                      <label htmlFor="terms" className="text-xs sm:text-sm text-muted-foreground cursor-pointer">
                        I agree to the{' '}
                        <Link to="/terms" className="underline underline-offset-2">
                          Terms &amp; Conditions
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="underline underline-offset-2">
                          Privacy Policy
                        </Link>
                        .
                      </label>
                    </div>

                    {/* Cloudflare Turnstile */}
                    {TURNSTILE_SITE_KEY && (
                      <div className="space-y-2">
                        <div
                          ref={turnstileRef}
                          className="cf-turnstile-widget"
                          data-sitekey={TURNSTILE_SITE_KEY}
                        />
                        <p className="text-xs text-muted-foreground text-center">
                          Protected by <span className="font-medium">Cloudflare Turnstile</span>
                        </p>
                      </div>
                    )}

                    {isSubmitting && (
                      <p className="text-xs text-muted-foreground text-center">
                        Submitting your audit request&hellip; please wait a moment while we process it.
                      </p>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full gradient-primary text-primary-foreground shadow-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Send Me My Free GEO/AIO Audit'
                      )}
                    </Button>

                    <p className="text-sm text-center text-muted-foreground">
                      Zero cost • No obligation • Delivered by email
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
